import * as React from 'react'

import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'

import type Document from 'modules/documents/store/models/Document'
import type {
  ResultMatchDataType,
  ResultMatches,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import {
  RESET_DATA,
  SHOW_RESULTS_FILES_MODAL,
  SHOW_UPLOAD_MODAL,
  UPDATE_DATA,
  UPLOAD_FILE,
} from 'modules/payroll_bundle/lib/constants'
import { ResultConcepts, ResultConceptsType } from 'modules/payroll_bundle/lib/constants'

export type ActionTypes =
  | 'RESET_DATA'
  | 'UPDATE_DATA'
  | 'SHOW_RESULTS_FILES_MODAL'
  | 'SHOW_UPLOAD_MODAL'

export interface ResetDataAction {
  type: 'RESET_DATA'
}

export interface UpdateDataAction {
  type: 'UPDATE_DATA'
  payload: Omit<Partial<State>, 'resultDispatch'>
}

export interface ShowResultsFilesModalAction {
  type: 'SHOW_RESULTS_FILES_MODAL'
}

export interface ShowUploadModalAction {
  type: 'SHOW_UPLOAD_MODAL'
}

export interface ResetDataAction {
  type: 'RESET_DATA'
}

export type Actions =
  | ResetDataAction
  | UpdateDataAction
  | ShowResultsFilesModalAction
  | ShowUploadModalAction

export type ImportEmployeeConcept = {
  concept_id: number
  label: string
  value: string
}

export enum MatchReason {
  name = 'name',
  none = 'none',
  identifier = 'id',
  company_identifier = 'company_identifier',
  database = 'db',
}

export type PayrollResultStagesKey = 'SELECT_DATA' | 'MAP_TO_CONCEPTS' | 'REVIEW_AND_IMPORT'

export interface ImportData {
  id: number
  userId?: number | null // To be deprecated
  employee_id: number | null
  net_salary: number | null
  current_net_salary?: number | null
  gross_salary: number | null
  current_gross_salary?: number | null
  company_costs: number | null
  current_company_costs?: number | null
  last_name: string
  first_name: string
  full_name: string
  company_identifier: string | null
  concepts: ImportEmployeeConcept[]
  match_reason?: MatchReason | null
  assigned_date?: number | null

  [key: string]: ImportEmployeeConcept[] | string | number | null | undefined
}

export interface State {
  filename: string
  document_id: number | null
  matchedData: ResultMatchDataType[]
  activeButton: string
  selectedFields: unknown[]
  worksheetData: WorksheetData
  worksheetMatches: ResultMatches
  showUploadModal: boolean
  showHistoryModal: boolean
  showResultsFilesModal: boolean
  selectedPayrollResultId: number | null
  importEmployeeIdToEmployeeId: { [key: string]: number } // manually assigned employees
  employeesSelected: Set<number> // FIXME: Rename, contains resultItems ids, not employee ids
  importedEmployees: ImportData[]
  disableUploadButton: boolean
  payrollResultsStage: string
  payrollImportId: string
  payrollConfigId: string
  uploadedPayrollResults: Document[]
  showListPayslipModal: boolean
  showPayslipUploadModal: boolean
  showResultValidationsSidePanel: boolean
  uploadedPayrollPayslips: Document[]
  resultConceptType: ResultConceptsType
  selectedEmployeeId: number | null
  sortedResultColumns: ResultConceptItemTableRowFragment['concept'][]
  resultDispatch: React.Dispatch<Actions>
}

const initialState = {
  filename: '',
  document_id: null,
  matchedData: [],
  activeButton: '',
  selectedFields: [],
  worksheetData: [],
  worksheetMatches: {},
  showUploadModal: false,
  showHistoryModal: false,
  showResultsFilesModal: false,
  selectedPayrollResultId: null,
  importEmployeeIdToEmployeeId: {},
  employeesSelected: new Set([]),
  importedEmployees: [],
  disableUploadButton: false,
  payrollResultsStage: UPLOAD_FILE,
  payrollImportId: '',
  payrollConfigId: '',
  uploadedPayrollResults: [],
  showListPayslipModal: false,
  showPayslipUploadModal: false,
  showResultValidationsSidePanel: false,
  uploadedPayrollPayslips: [],
  resultConceptType: ResultConcepts.regular_payroll,
  selectedEmployeeId: null,
  sortedResultColumns: [],
  resultDispatch: () => null,
}

const payrollResultsContext = React.createContext<State>(initialState)

const PayrollResultsProvider = payrollResultsContext.Provider

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case UPDATE_DATA:
      return { ...state, ...action.payload }

    case SHOW_UPLOAD_MODAL:
      return { ...initialState, showUploadModal: true }

    case SHOW_RESULTS_FILES_MODAL:
      return { ...initialState, showResultsFilesModal: true }

    case RESET_DATA:
      return { ...initialState, employeesSelected: new Set([]) }

    default:
      throw new Error()
  }
}

interface Props {
  children: React.ReactNode
}

export const PayrollResultsContextProvider = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer<React.Reducer<State, Actions>>(reducer, initialState)

  return (
    <PayrollResultsProvider value={{ ...state, resultDispatch: dispatch }}>
      {children}
    </PayrollResultsProvider>
  )
}

export const usePayrollResults = () => {
  const results = React.useContext(payrollResultsContext)

  if (!results) {
    throw new Error('usePayrollResults hook must be used within a PayrollResultsProvider provider')
  }

  return results
}
